/**
 * Phase 2 · Shared album-card visual primitives.
 *
 * Three components rendered identically across MyAlbums, MyProfile
 * ("Your World Cup Collection"), CollectorProfilePreview and the
 * Museum:
 *
 *   • <AlbumStatusPill status="album_only" | "in_progress" | "completed" />
 *   • <AlbumProgressBar album={...} showPercent />
 *   • <AlbumNicknameEditor value onSave readOnly />
 *
 * The NicknameEditor is fully self-contained. It:
 *   • enters edit mode on click of the pencil affordance
 *   • saves on blur / Enter, cancels on Escape
 *   • calls the supplied `onSave(nextValue)` promise (parent handles
 *     the PATCH — this keeps the component API-agnostic)
 *   • surfaces validation errors via the returned promise rejecting
 *   • never propagates click events out of edit controls (so a card
 *     wrapped in a <Link> is not accidentally navigated)
 */
import React, { useEffect, useRef, useState } from "react";
import { Pencil, Check, X, Loader2 } from "lucide-react";
import { deriveProgress } from "@/lib/albumStatus";

const PILL_STYLES = {
  album_only:  "bg-neutral-100 text-neutral-700 border-neutral-400",
  in_progress: "bg-[var(--panini-yellow)] text-[var(--ink)] border-[var(--ink)]",
  completed:   "bg-emerald-100 text-emerald-900 border-emerald-900",
};

const PILL_LABELS = {
  album_only:  "Album Only",
  in_progress: "In Progress",
  completed:   "Completed",
};

export function AlbumStatusPill({ status, className = "", testid }) {
  const cls = PILL_STYLES[status] || PILL_STYLES.in_progress;
  const label = PILL_LABELS[status] || PILL_LABELS.in_progress;
  return (
    <span
      data-testid={testid}
      data-status={status}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border-2 font-bold text-[10px] uppercase tracking-widest ${cls} ${className}`}
    >
      {label}
    </span>
  );
}

/**
 * Progress bar with derived state (empty / partial / full).
 * When status resolves to "album_only", the bar is not rendered.
 */
export function AlbumProgressBar({ album, showPercent = true, testid }) {
  const p = deriveProgress(album);
  if (p.state === "hidden") return null;
  const pct = p.percent ?? 0;
  return (
    <div data-testid={testid} data-progress-state={p.state} className="w-full">
      <div className={`h-2 rounded-full overflow-hidden ${p.trackClass}`}
           role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className={`h-full ${p.fillClass} transition-[width]`}
             style={{ width: `${pct}%` }}
             data-testid={testid ? `${testid}-fill` : undefined} />
      </div>
      {showPercent && (
        <div className="mt-1 text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)] flex items-center justify-between">
          <span>Progress</span>
          <span className="text-[var(--ink)]" data-testid={testid ? `${testid}-percent` : undefined}>
            {pct}%
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Inline editable nickname field.
 *
 *   • read-only mode  →  { nickname || fallback } + Pencil affordance
 *   • edit mode       →  <input> + Check / X buttons + spinner
 *
 * onSave: async (nextValue) => Promise
 *   Parent is responsible for the PATCH and reload. If the promise
 *   rejects, the input remains in edit mode and displays a small
 *   inline error under the field.
 *
 * Every interactive element stops propagation so wrapping the card
 * in <Link> or an onClick container never navigates on click of the
 * pencil / input.
 */
export function AlbumNicknameEditor({
  value,
  onSave,
  readOnly = false,
  placeholder = "Add a nickname (e.g. My USA Hardcover)",
  maxLength = 120,
  testid = "album-nickname",
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };

  const commit = async (e) => {
    if (e) stop(e);
    const next = (draft || "").trim();
    const prev = (value || "").trim();
    if (next === prev) { setEditing(false); return; }
    setBusy(true); setError("");
    try {
      await onSave(next);
      setEditing(false);
    } catch (err) {
      setError(err?.message || "Could not save.");
    } finally { setBusy(false); }
  };

  const cancel = (e) => {
    if (e) stop(e);
    setDraft(value || "");
    setError("");
    setEditing(false);
  };

  if (!editing) {
    const shown = (value || "").trim();
    return (
      <div className="flex items-center gap-2 min-w-0" data-testid={testid}>
        {shown ? (
          <span className="text-sm italic text-[var(--smoke)] truncate min-w-0"
                data-testid={`${testid}-value`}>
            &ldquo;{shown}&rdquo;
          </span>
        ) : (
          <span className="text-xs text-[var(--smoke)] italic truncate min-w-0"
                data-testid={`${testid}-empty`}>
            {placeholder}
          </span>
        )}
        {!readOnly && (
          <button
            type="button"
            onClick={(e) => { stop(e); setEditing(true); }}
            className="p-1 rounded hover:bg-[var(--panini-yellow-soft)] text-[var(--smoke)] hover:text-[var(--ink)] transition"
            aria-label="Edit nickname"
            data-testid={`${testid}-edit`}
          >
            <Pencil size={12} />
          </button>
        )}
      </div>
    );
  }

  return (
    <form
      onSubmit={commit}
      onClick={stop}
      className="flex items-center gap-1.5 min-w-0"
      data-testid={`${testid}-form`}
    >
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") { cancel(e); }
        }}
        onClick={stop}
        maxLength={maxLength}
        disabled={busy}
        placeholder={placeholder}
        className="border-2 border-[var(--ink)] rounded-md px-2 py-1 text-sm bg-white flex-1 min-w-0"
        data-testid={`${testid}-input`}
      />
      <button
        type="submit"
        disabled={busy}
        onClick={commit}
        className="p-1 rounded bg-emerald-600 text-white hover:brightness-110 disabled:opacity-60"
        aria-label="Save nickname"
        data-testid={`${testid}-save`}
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
      </button>
      <button
        type="button"
        onClick={cancel}
        disabled={busy}
        className="p-1 rounded bg-white border-2 border-[var(--ink)] text-[var(--ink)] hover:bg-neutral-50 disabled:opacity-60"
        aria-label="Cancel edit"
        data-testid={`${testid}-cancel`}
      >
        <X size={12} />
      </button>
      {error && (
        <span className="text-[10px] text-[var(--panini-red)]" data-testid={`${testid}-error`}>
          {error}
        </span>
      )}
    </form>
  );
}
